import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clientsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import {
  parseClientIdParam,
  readLastSelectedClientId,
  writeLastSelectedClientId,
} from '../utils/lastSelectedClientStorage';

const ClientContext = createContext(null);

export function ClientProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [activeClients, setActiveClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [activeLoaded, setActiveLoaded] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [allError, setAllError] = useState(null);
  const [lastSelectedClientId, setLastSelectedClientIdState] = useState(null);

  const setLastSelectedClientId = useCallback(
    (clientId) => {
      const parsed = parseClientIdParam(clientId);
      setLastSelectedClientIdState(parsed);
      if (isAuthenticated) {
        writeLastSelectedClientId(user?.id, parsed);
      }
    },
    [isAuthenticated, user?.id],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setLastSelectedClientIdState(null);
      return;
    }
    setLastSelectedClientIdState(readLastSelectedClientId(user?.id));
  }, [isAuthenticated, user?.id]);

  const refreshClients = useCallback(
    async ({ includeArchived = false, force = false } = {}) => {
      if (!isAuthenticated) return [];

      const loaded = includeArchived ? allLoaded : activeLoaded;
      if (loaded && !force) {
        return includeArchived ? allClients : activeClients;
      }

      const setLoading = includeArchived ? setAllLoading : setActiveLoading;
      const setError = includeArchived ? setAllError : setActiveError;
      const setLoaded = includeArchived ? setAllLoaded : setActiveLoaded;
      const setRows = includeArchived ? setAllClients : setActiveClients;

      setLoading(true);
      setError(null);
      try {
        const rows = await clientsAPI.getClients(includeArchived);
        const normalized = Array.isArray(rows) ? rows : [];
        setRows(normalized);
        setLoaded(true);
        return normalized;
      } catch (error) {
        setRows([]);
        setError(error?.message || 'Failed to load clients');
        setLoaded(true);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [activeClients, activeLoaded, allClients, allLoaded, isAuthenticated],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setActiveClients([]);
      setAllClients([]);
      setActiveLoaded(false);
      setAllLoaded(false);
      return;
    }

    refreshClients();
  }, [isAuthenticated, refreshClients]);

  const value = useMemo(
    () => ({
      activeClients,
      allClients,
      activeLoaded,
      allLoaded,
      activeLoading,
      allLoading,
      activeError,
      allError,
      lastSelectedClientId,
      setLastSelectedClientId,
      refreshClients,
    }),
    [
      activeClients,
      allClients,
      activeLoaded,
      allLoaded,
      activeLoading,
      allLoading,
      activeError,
      allError,
      lastSelectedClientId,
      setLastSelectedClientId,
      refreshClients,
    ],
  );

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

export function useClients(includeArchived = false) {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClients must be used within a ClientProvider');
  }

  const {
    activeClients,
    allClients,
    activeLoaded,
    allLoaded,
    activeLoading,
    allLoading,
    activeError,
    allError,
    lastSelectedClientId,
    setLastSelectedClientId,
    refreshClients,
  } = context;

  useEffect(() => {
    if (includeArchived && !allLoaded && !allLoading) {
      refreshClients({ includeArchived: true });
    }
  }, [allLoaded, allLoading, includeArchived, refreshClients]);

  return {
    clients: includeArchived ? allClients : activeClients,
    loading: includeArchived ? allLoading : activeLoading,
    error: includeArchived ? allError : activeError,
    loaded: includeArchived ? allLoaded : activeLoaded,
    lastSelectedClientId,
    setLastSelectedClientId,
    refreshClients,
  };
}
